import classNames from 'classnames';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';

import Icons from 'assets/icons';

import { useOpen } from 'hooks/use-open';

import type { MarkContext } from '../mark-panel.types';

export interface MarkContextChipProps {
  context: MarkContext;
}

const MarkContextChip = ({ context }: MarkContextChipProps) => {
  const { isOpen, handleToggle } = useOpen(false);
  const { summary } = context;

  if (!summary) {
    return (
      <Box sx={{ width: '20rem' }}>
        <Stack
          direction="row"
          sx={{
            width: '100%',
            minWidth: 0,
            alignItems: 'center',
            borderRadius: '0.375rem',
            borderWidth: '1px',
            borderColor: 'tokens.navy.100',
            bgcolor: 'common.white',
            px: 1.5,
            py: 0.75,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            fontWeight: 600,
            color: 'tokens.navy.600',
          }}
          title={context.label}
        >
          <span className="min-w-0 truncate">{context.label}</span>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack sx={{ width: '20rem', gap: 0.75 }}>
      <Button
        variant="reserve-outlined"
        size="sm"
        className={classNames('w-full min-w-0 justify-between', {
          'border-navy-100 text-navy-600': !isOpen,
          'border-navy-800 text-navy-800 shadow': isOpen,
        })}
        iconAfter={
          <Icons.ChevronDown
            className={classNames('size-3.5 transition-transform', {
              'rotate-180': isOpen,
            })}
            aria-hidden="true"
          />
        }
        onClick={handleToggle}
        aria-expanded={isOpen}
        title={context.label}
      >
        <span className="min-w-0 truncate">{context.label}</span>
      </Button>

      {isOpen && (
        <Stack
          sx={{
            gap: 0.75,
            borderRadius: '0.375rem',
            bgcolor: 'tokens.gray.50',
            p: 1.5,
          }}
        >
          <p className="break-words text-sm font-semibold text-gray-700">
            {summary.title}
          </p>

          <dl className="flex flex-col gap-1.5">
            {summary.rows.map((row) => (
              <Stack
                key={row.label}
                direction="row"
                sx={{
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <dt className="shrink-0 text-xs text-gray-500">{row.label}</dt>
                <dd className="min-w-0 break-words text-right text-xs font-semibold text-gray-700">
                  {row.value}
                </dd>
              </Stack>
            ))}
          </dl>
        </Stack>
      )}
    </Stack>
  );
};

export default MarkContextChip;
